import { useEffect } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { GppBad as ShieldIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { colors } from '@/theme/tokens';

export function NoPermissionPage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Force logout when user hits the browser back button from this screen —
  // otherwise they'd bounce straight back to Access Denied (the previous
  // route is still behind an auth guard that routes them here) and the app
  // feels stuck. Logging out on back takes them cleanly to /login.
  useEffect(() => {
    // Push a sentinel state so the first `popstate` fires on this page,
    // not on whatever was before it in history.
    window.history.pushState({ noPerm: true }, '');
    const onPopState = () => {
      logout();
      navigate('/login', { replace: true });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [logout, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F7F8FA',
        gap: 3,
        p: 3,
      }}
    >
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: colors.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>A</Typography>
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111' }}>Aviron MDM</Typography>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          bgcolor: '#fff',
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          p: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ShieldIcon sx={{ fontSize: 40, color: colors.error.main }} />
        </Box>

        {/* Title & Description */}
        <Typography variant="h5" fontWeight={700} textAlign="center">
          Access Denied
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 380, lineHeight: 1.5 }}
        >
          Your account does not have permission to access Aviron MDM. Please contact your
          administrator to request access.
        </Typography>

        {/* Divider */}
        <Box sx={{ width: '100%', height: '1px', bgcolor: '#E5E7EB' }} />

        {/* Account Details */}
        <Box
          sx={{
            width: '100%',
            bgcolor: '#F4F4F4',
            borderRadius: 1,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Your Account Details
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography variant="caption" fontWeight={500}>{user?.email || '-'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Organization</Typography>
            <Typography variant="caption" fontWeight={500}>{user?.organization || '-'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Role</Typography>
            <Chip label="No MDM Role Assigned" size="small" sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontSize: 12 }} />
          </Box>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
          <Button variant="outlined" fullWidth onClick={handleSignOut}>
            Sign Out
          </Button>
        </Box>
      </Box>

      {/* Footer hint */}
      <Typography
        variant="caption"
        color="text.secondary"
        textAlign="center"
        sx={{ maxWidth: 480, lineHeight: 1.5 }}
      >
        Aviron MDM requires an assigned role to access the platform. Ask your admin to assign you
        one of: Admin, Manager, or Support.
      </Typography>
    </Box>
  );
}
